import { FC } from 'react';
import { VirtualItem } from '@tanstack/react-virtual';
import { Page } from '@/api';
import { calculateA4HeightAndAspectRatio } from '@/utils';
import { PageView } from './PageView';

interface PdfPageGridProps {
  rowItems: VirtualItem[];
  pages: Page[];
  columnSize: number;
  gapSize: number;
  /** Default per-page width; used as a fallback when `rowPageWidths` doesn't have an entry. */
  dynamicWidth: number;
  /** Optional per-row width override. Lets each row pick a different page width
   *  (e.g. so a row of landscape pages can fill more of the column budget than a
   *  row containing tall portrait pages would allow). */
  rowPageWidths?: number[];
  storePageScale: number;
  isSelected: (index: number) => boolean;
  isPageSelectedFn: (index: number) => boolean;
  onPageClick: (e: React.MouseEvent<HTMLDivElement>, pageIndex: number, pageItem: Page) => void;
  setSelectedIndex: (index: number) => void;
  parentRef: React.RefObject<HTMLDivElement | null>;
  isScrolling?: boolean; // New: Track scrolling state
}

export const PdfPageGrid: FC<PdfPageGridProps> = ({
  rowItems,
  pages,
  columnSize,
  gapSize,
  dynamicWidth,
  rowPageWidths,
  storePageScale,
  isSelected,
  isPageSelectedFn,
  onPageClick,
  setSelectedIndex,
  parentRef,
  isScrolling = false, // Default to false for backward compatibility
}) => {
  return (
    <>
      {rowItems.map((virtualRow) => {
        const rowPages = pages.slice(
          virtualRow.index * columnSize,
          Math.min((virtualRow.index + 1) * columnSize, pages.length),
        );
        // Per-row width if available (2-col layout), else the uniform default.
        const pageBudget = rowPageWidths?.[virtualRow.index] ?? dynamicWidth;

        return (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            style={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: `translateY(${virtualRow.start}px) translateX(-50%)`,
              width: '100%',
              height: `${virtualRow.size}px`,
            }}
          >
            <div
              className="flex items-start justify-center px-4 py-2"
              style={{
                gap: `${gapSize}px`,
                minHeight: 'fit-content',
              }}
            >
              {rowPages.map((pageItem: Page, colIndex: number) => {
                const globalPageIndex = virtualRow.index * columnSize + colIndex;
                const isKeyboardSelected = isSelected(globalPageIndex);
                const isMultiSelected = isPageSelectedFn(globalPageIndex);

                const isHighlighted = isMultiSelected || isKeyboardSelected;
                return (
                  <div
                    key={`${virtualRow.index}-${colIndex}`}
                    className={`flex cursor-pointer flex-col items-center rounded-[8px] transition-all duration-150 select-none ${
                      isHighlighted
                        ? 'bg-fletchers-green-50 ring-fletchers-green-500 ring-2'
                        : 'hover:ring-fletchers-gray-150 hover:ring-1'
                    }`}
                    style={{
                      width: `${pageBudget}px`,
                      flex: `0 0 ${pageBudget}px`,
                      padding: '8px',
                    }}
                    onClick={(e) => {
                      // Prevent text selection when shift+click is used for range selection
                      if (e.shiftKey) {
                        e.preventDefault();
                      }

                      // Handle the page click through the parent callback
                      onPageClick(e, globalPageIndex, pageItem);

                      // Set keyboard navigation selection only for regular clicks (not multi-select)
                      if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
                        setSelectedIndex(globalPageIndex);
                      }

                      // Focus the container to enable keyboard navigation
                      if (parentRef.current) {
                        parentRef.current.focus();
                      }
                    }}
                  >
                    {/* Conditional rendering: Pause PDF rendering during scroll (like bundle page) */}
                    {!isScrolling ? (
                      <PageView
                        page={pageItem}
                        width={pageBudget - 16} // Subtract 8px wrapper padding on each side
                        scale={storePageScale}
                      />
                    ) : (
                      // Placeholder during scroll — sized to the page's true aspect ratio
                      // so the placeholder height matches the rendered PageView height
                      // (no jump when scroll stops). Falls back to A4 when dimensions
                      // are missing/zero (handled inside calculateA4HeightAndAspectRatio).
                      (() => {
                        const placeholderWidth = pageBudget - 16;
                        const placeholderHeight =
                          calculateA4HeightAndAspectRatio({
                            width: pageItem.pageDimensions?.width,
                            height: pageItem.pageDimensions?.height,
                            newWidth: placeholderWidth,
                          }) * storePageScale;
                        return (
                          <div
                            className="flex items-center justify-center rounded bg-gray-200 text-gray-500"
                            style={{
                              width: `${placeholderWidth}px`,
                              height: `${placeholderHeight}px`,
                            }}
                          >
                            <span className="text-sm">Page {pageItem.customOrdering}</span>
                          </div>
                        );
                      })()
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
};
